// src/components/order/ProductSelectionModal.jsx

import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { toast } from 'react-toastify';

Modal.setAppElement('#root');

const ProductSelectionModal = ({ isOpen, onClose, products, onSelectProduct }) => {
    // --- STATE MANAGEMENT (Simplified) ---
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedProducts, setSelectedProducts] = useState({});
    const [viewImageUrl, setViewImageUrl] = useState(null);



    // Reset state when the modal closes
    useEffect(() => {
    if (!isOpen) {
        setSearchQuery("");
        setSelectedProducts({});
    }
}, [isOpen]);


    // --- HELPERS ---
    const filteredProducts = products.filter(product =>
        product.designName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // // --- HANDLERS ---
    // const handleProductClick = (product) => {
    //     // If clicking a different product, reset size.
    //     if (selectedProduct?.id !== product.id) {
    //         setSelectedProduct(product);
    //         setSelectedSize("");
    //     }
    // };

    const toggleProduct = (product) => {
    setSelectedProducts(prev => {
        const exists = !!prev[product.id];
        if (exists) {
            const newState = { ...prev };
            delete newState[product.id];
            return newState;
        } else {
            return {
                ...prev,
                [product.id]: { product, sizes: [] }
            };
        }
    });
};
const toggleSize = (productId, size) => {
    setSelectedProducts(prev => {
        const productEntry = prev[productId];
        if (!productEntry) return prev;

        const sizes = productEntry.sizes.includes(size)
            ? productEntry.sizes.filter(s => s !== size)
            : [...productEntry.sizes, size];

        return {
            ...prev,
            [productId]: { ...productEntry, sizes }
        };
    });
};

// Add getSKUForSize helper
const getSKUForSize = (skus, size) => {
    if (!skus || !Array.isArray(skus)) return null;
    return skus.find(sku => {
        const parts = sku.split('-');
        return (parts.length >= 3 && parts[parts.length - 2] === size) || (parts[parts.length - 1] === size);
    });
};

const handleConfirmSelection = () => {
    const validSelections = Object.values(selectedProducts).filter(
        (entry) => entry.sizes.length > 0
    );

    if (validSelections.length === 0) {
        toast.warn("Please select at least one product with size(s)!");
        return;
    }

    // Flatten each product with each selected size into separate product entries
    const flattenedProducts = validSelections.flatMap(entry =>
        entry.sizes.map(size => {
            // Assign SKU for each product/size
            const selectedSKU = getSKUForSize(entry.product.SKUs, size);
            if (!selectedSKU) {
                toast.error(`SKU not found for size ${size} in ${entry.product.designName}.`);
                return null;
            }
            return {
                ...entry.product,
                selectedSize: size,
                selectedSKU,
                quantity: 1 // Default quantity per product-size combo
            };
        })
    );

    // If any product is null (SKU missing), do not proceed
    if (flattenedProducts.some(p => p === null)) {
        return;
    }

    onSelectProduct(flattenedProducts);
    onClose();
};


    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            style={{
                overlay: { backgroundColor: 'rgba(0, 0, 0, 0.75)', zIndex: 50 },
                content: {
                    top: '50%', left: '50%', right: 'auto', bottom: 'auto',
                    marginRight: '-50%', transform: 'translate(-50%, -50%)',
                    border: 'none', background: 'transparent', padding: '0',
                    width: '95%', maxWidth: '600px' // Can be narrower now
                }
            }}
            contentLabel="Select a Product"
        >
            <div className="bg-white rounded-lg shadow-xl flex flex-col max-h-[90vh]">
                {/* Modal Header */}
                <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white z-10">
                    <h2 className="text-xl font-semibold text-gray-800">Select Product</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-3xl leading-none">×</button>
                </div>

                {/* Main Content Area (Single Column) */}
                <div className="p-4 flex-grow overflow-y-auto">
                    <input
                        type="text"
                        placeholder="Search by design name..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full p-2 border rounded-md mb-4 sticky top-0"
                    />
                    <div className="space-y-3">
                        {filteredProducts.map((product) => {
    const isSelected = !!selectedProducts[product.id];
    const selectedSizes = selectedProducts[product.id]?.sizes || [];

    return (
<div
  key={product.id}
  className={`p-4 border rounded-lg transition-all ${
    isSelected ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' : 'hover:border-gray-400'
  }`}
>
  <div className="flex items-center gap-4">
    <div className="relative w-16 h-16 flex-shrink-0 rounded-md overflow-hidden border border-gray-300">
      <img
        src={product.images.front}
        alt={product.designName}
        className="w-full h-full object-cover"
      />
      <button
        onClick={e => {
          e.stopPropagation(); // prevent toggleProduct click
          setViewImageUrl(product.images.front);
        }}
        title="View larger"
        className="absolute bottom-1 right-1 bg-black bg-opacity-50 text-white p-1 rounded-full hover:bg-opacity-75 transition"
        style={{ fontSize: '0.7rem', lineHeight: 1 }}
      >
        🔍
      </button>
    </div>

    <div className="cursor-pointer flex-1" onClick={() => toggleProduct(product)}>
      <p className="font-semibold text-gray-800">{product.productName} - {product.designName}</p>
      <p className="text-sm text-gray-500">Price: ₹{product.totalPrice}</p>
    </div>
  </div>

  {isSelected && (
    <div className="mt-4 pt-4 border-t border-blue-200">
      <p className="text-sm font-semibold text-gray-700 mb-2">Select Size(s):</p>
      <div className="flex flex-wrap gap-2">
{(() => {
  const isBabyTee = product.productName.toLowerCase().includes('baby tee');
  const availableSizes = isBabyTee
    ? ['XS', 'S', 'M', 'L', 'XL', 'XXL']
    : ['S', 'M', 'L', 'XL', 'XXL'];

  return availableSizes.map(size => {
    const isActive = selectedSizes.includes(size);
    return (
      <button
        key={size}
        onClick={() => toggleSize(product.id, size)}
        className={`px-4 py-1 text-sm border rounded-full font-medium transition-colors duration-200 ease-in-out ${
          isActive
            ? 'bg-blue-600 text-white border-blue-600'
            : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
        }`}
      >
        {size}
      </button>
    );
  });
})()}

      </div>
    </div>
  )}
</div>

    );
})}

                         {filteredProducts.length === 0 && (
                            <div className="text-center py-10 text-gray-500">
                                No products match your search.
                            </div>
                        )}
                    </div>
                </div>

                {/* Modal Footer */}
                <div className="p-4 border-t text-right sticky bottom-0 bg-white z-10">
                    <button
                        onClick={handleConfirmSelection}
                        disabled={Object.values(selectedProducts).every(p => p.sizes.length === 0)}
                        className="bg-blue-600 text-white px-8 py-2 rounded-md font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                        Confirm Selection
                    </button>
                </div>
            </div>
{viewImageUrl && (
  <div
    className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 pt-20" 
   style={{
    scrollbarWidth: 'none',        // Firefox
    msOverflowStyle: 'none',       // IE 10+
  }}>
    <button
      onClick={() => setViewImageUrl(null)}
      className="absolute top-5 right-5 text-white bg-black bg-opacity-60 hover:bg-opacity-90 rounded-full p-2 z-60"
      aria-label="Close image preview"
    >
      ✕
    </button>
    <img
      src={viewImageUrl}
      alt="Product Large View"
      className="max-w-[90vw] max-h-[90vh] rounded-lg shadow-lg"
    />
  </div>
)}


        </Modal>

        


    );
};

export default ProductSelectionModal;